import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { initialize as initializeGA, pageview } from '@tempus/t-shared/ui/utils/gaHelpers';
import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, matchPath, useParams } from 'react-router-dom';
import { ALL_SITES } from '@tempus/trials-types';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import CheckRole from '~/auth/CheckRole';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { config } from '~/config';
import { Routes as LinkRoutes, RouteConfig } from '~/routes/';
import paths, { getAllPatientsPageUrl } from '~/routes/paths';
import { canAccessCohortBuilder } from '~/routes/builder/builderPermissions';
import { canAccessProgramManagement } from '~/routes/programManagement/programManagementPermissions';
import { useSelector, useDispatch } from '~/store';
import { creators as userAction } from '~/store/enhanceUser';
import { useFeatureEnabled } from './_core/FeatureGate/FeatureGate';
import { FEATURE_FLAG } from '~/constants/feature-flags';
import {
  canViewProviderReporting,
  getTrialsLandingPath,
  isSessionTimeoutModalOn,
  isTrialTabsHidden,
} from '~/utils/toggleHelpers';
import { Navigation } from '~/components/_core/Navigation/Navigation';
import { PATIENT_WORKSPACE_BREADCRUMB_HOST_ELEMENT_ID } from '~/routes/PatientWorkspacePage/PatientWorkspaceBreadcrumbs';

import AntdThemeProvider from '~/components/_antdOverrides/AntdThemeProvider/AntdThemeProvider';
import SessionTimeoutManager from '~/components/_core/SessionTimeoutManager/SessionTimeoutManager';
import ErrorBoundary from '~/components/_core/ErrorBoundary/ErrorBoundary';
import { LocaleProvider } from '~/i18n';
import '../assets/styles/main.css';

const SitePatientTrackerRedirect: React.FC = () => {
  const { siteId } = useParams<{ siteId?: string }>();
  const location = useLocation();
  const targetPath = siteId != null && siteId !== '' ? getAllPatientsPageUrl(siteId) : getAllPatientsPageUrl(ALL_SITES);

  return <Navigate to={`${targetPath}${location.search}`} replace state={location.state} />;
};

const generateRoute = (route: RouteConfig, enabled: boolean, isNestedRoute = false) => {
  if (!enabled) {
    return null;
  }

  return (
    <Route
      path={route.path}
      element={
        <CheckRole
          redirect
          Component={route.component}
          authorizedRoles={route.authorizedRoles ?? []}
          showSiteSelection={route.showSiteSelection}
          isNestedRoute={isNestedRoute}
        />
      }
    >
      {route.children && route.children.map((childRoute) => generateRoute(childRoute, enabled, true))}
    </Route>
  );
};

const usePageViews = () => {
  const location = useLocation();
  useEffect(() => pageview(location.pathname), [location]);
};

const queryClient = new QueryClient();

const App: React.FC = () => {
  usePageViews();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useAppNavigate();

  const [initialUrl, setInitialUrl] = useState<{
    pathname: string;
    search: string;
  } | null>(null);

  const canViewNewDocuments = useSelector(({ user }) => user.canViewNewDocuments);
  const canViewPatientTracking = useSelector(({ user }) => user.canViewPatientTracking);
  const canAccessPatientWorkspace = useSelector(({ user }) => user.canAccessPatientWorkspace);
  const canViewSiteDocuments = useSelector(({ user }) => user.canViewSiteDocuments);
  const hasViewCarePathwaysRole = useSelector(({ user }) => user.canViewCarePathways);
  const features = useSelector(({ user }) => user.features);

  const effectiveRoles = useSelector(({ user }) => user.effectiveRoles);
  const canViewBuilder = canAccessCohortBuilder(features, effectiveRoles);
  const isCarePathwaysEnabled = useFeatureEnabled(FEATURE_FLAG.CARE_PATHWAYS);
  const canViewCarePathways = hasViewCarePathwaysRole && isCarePathwaysEnabled;
  const showProviderReporting = canViewProviderReporting(isCarePathwaysEnabled);
  const canViewProgramManagement = canAccessProgramManagement(effectiveRoles);

  useEffect(() => {
    (async () => await initializeGA(config.gaTrackingId))();
    dispatch(userAction.getCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    if (!initialUrl) {
      // set the initial url with query
      setInitialUrl({
        pathname: location.pathname,
        search: location.search,
      });
    }
  }, [initialUrl, location.pathname, location.search]);

  useEffect(() => {
    // if the user has features flags, redirect to the initial url
    if (features.length && initialUrl?.pathname && initialUrl?.pathname !== location.pathname) {
      navigate(initialUrl?.pathname + initialUrl?.search, { replace: true });
    }
  }, [features]);

  const patientWorkspaceBreadcrumbHost = matchPath({ path: paths.PATIENT_V2, end: false }, location.pathname) != null;
  const showAdminPage = config.environment !== 'prd';
  const trialsLandingPath = getTrialsLandingPath();
  const hideTrialTabs = isTrialTabsHidden();

  return (
    <LocaleProvider>
      <AntdThemeProvider appearance="lightCarbon">
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <QueryClientProvider client={queryClient}>
            {
              isSessionTimeoutModalOn() && (
                <SessionTimeoutManager />
              ) /* Inactivity and Session expiration warning modal */
            }
            <Navigation />
            {patientWorkspaceBreadcrumbHost ? (
              <div id={PATIENT_WORKSPACE_BREADCRUMB_HOST_ELEMENT_ID} style={{ flexShrink: 0, width: '100%' }} />
            ) : null}
            <div
              style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                isolation: 'isolate',
                minHeight: 0,
                minWidth: 400,
                position: 'relative',
                zIndex: 0,
              }}
            >
              <ErrorBoundary>
                <QueryParamProvider adapter={ReactRouter6Adapter}>
                  <main
                    style={{
                      display: 'flex',
                      flex: 1,
                      flexDirection: 'column',
                      minHeight: 0,
                      overflow: 'hidden',
                    }}
                  >
                    {/*
                  Non-scrolling `main` (ADR 0001). Root routes use `RouteShell` in CheckRole; nested
                  outlets render without a shell. PageLayout routes fill the shell and scroll in-pane.
                */}
                    <div
                      style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden',
                        width: '100%',
                      }}
                    >
                      <Routes>
                        {generateRoute(LinkRoutes.activationPacketSubmission, true)}
                        {generateRoute(LinkRoutes.admin, showAdminPage)}
                        {generateRoute(LinkRoutes.allPatientsPage, canViewPatientTracking)}
                        {generateRoute(LinkRoutes.builder, canViewBuilder)}
                        {generateRoute(LinkRoutes.builderProject, canViewBuilder)}
                        {generateRoute(LinkRoutes.builderProjectStages, canViewBuilder)}
                        {generateRoute(LinkRoutes.builderEvaluationJobDetail, canViewBuilder)}
                        {generateRoute(LinkRoutes.builderPatientQueryJobDetail, canViewBuilder)}
                        {generateRoute(LinkRoutes.builderRulesetEvaluationJobs, canViewBuilder)}
                        {generateRoute(LinkRoutes.builderRuleset, canViewBuilder)}
                        {generateRoute(LinkRoutes.carePathways, canViewCarePathways)}
                        {generateRoute(LinkRoutes.pathwayPatients, canViewCarePathways)}
                        {generateRoute(LinkRoutes.programManagement, canViewProgramManagement)}
                        {generateRoute(LinkRoutes.programManagementSite, canViewProgramManagement)}
                        {generateRoute(LinkRoutes.programManagementPathway, canViewProgramManagement)}
                        {generateRoute(LinkRoutes.matchReviewPage, canViewPatientTracking)}
                        {generateRoute(LinkRoutes.carePathwayPatientPage, canAccessPatientWorkspace)}
                        {generateRoute(LinkRoutes.patientWorkspacePage, canAccessPatientWorkspace)}
                        {generateRoute(LinkRoutes.patientTrackerSites, canViewPatientTracking)}
                        {generateRoute(LinkRoutes.patientTrialEligibilityReview, true)}
                        {generateRoute(LinkRoutes.providerReporting, showProviderReporting)}
                        {generateRoute(LinkRoutes.providerReportingDashboard, showProviderReporting)}
                        {generateRoute(LinkRoutes.siteDocuments, canViewSiteDocuments)}
                        {generateRoute(LinkRoutes.tempusNursePage, canViewPatientTracking)}
                        {generateRoute(LinkRoutes.carePathwayPrescreening, canViewCarePathways)}
                        {generateRoute(LinkRoutes.timeScreeningTrials, true)}
                        {generateRoute(LinkRoutes.trialDocuments, canViewNewDocuments)}
                        {generateRoute(LinkRoutes.siteTrial, canViewPatientTracking)}
                        <Route path={paths.TRIALS} element={<Navigate to={trialsLandingPath} replace />} />
                        {hideTrialTabs ? (
                          <Route path={paths.TRIAL_SEARCH} element={<Navigate to={paths.TRIAL_MATERIALS} replace />} />
                        ) : null}
                        {generateRoute(LinkRoutes.trials, true)}
                        {generateRoute(LinkRoutes.userNotificationPreferences, true)}
                        <Route path={paths.SITE_PATIENT_TRACKER} element={<SitePatientTrackerRedirect />} />
                        <Route
                          path={paths.TIME_SCREENING_TRIALS}
                          element={<Navigate to={paths.TIME_SCREENING_TRIALS_MATCHING} replace />}
                        />
                        {showAdminPage ? (
                          <Route path={paths.BUILD_STATUS} element={<Navigate to={paths.ADMIN} replace />} />
                        ) : null}
                        <Route path="*" element={<Navigate to={trialsLandingPath} replace />} />
                      </Routes>
                    </div>
                  </main>
                </QueryParamProvider>
              </ErrorBoundary>
            </div>
          </QueryClientProvider>
        </div>
      </AntdThemeProvider>
    </LocaleProvider>
  );
};

export default App;
